import { Add, Close, Edit } from '@mui/icons-material';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import MonitorRoundedIcon from '@mui/icons-material/MonitorRounded';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

const SEVERITY_LEVELS = [
  'Information',
  'Trivial',
  'Minor',
  'Medium',
  'Major',
  'Critical',
];

const SEVERITY_COLORS = {
  Critical: '#FF3838',
  Major: '#FF3838',
  Medium: '#FFB302',
  Minor: '#FCE83A',
  Trivial: '#56F000',
  Information: '#2DCCFF',
  Unknown: 'transparent',
};

const EditAlarmGroupDialog = ({
  open,
  onClose,
  onUpdate,
  onDelete,
  openCreate,
  userOptions,
  alarmTypeOptions,
  alarmGroups,
}) => {
  const [groupToEdit, setGroupToEdit] = useState(null);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [groupName, setGroupName] = useState('');
  const [users, setUsers] = useState([]);
  const [alarmTypes, setAlarmTypes] = useState([]);

  useEffect(() => {
    if (groupToEdit) {
      setGroupName(groupToEdit.Name);
      setUsers(
        groupToEdit.AlarmSubscriptions.map((sub) => {
          const user = userOptions.find(
            (opt) => opt.User.UserId === sub.User.UserId,
          );
          return {
            user: user?.User?.Email || '',
            feedSeverity: sub.FeedSeverity,
            emailSeverity: sub.EmailSeverity,
          };
        }),
      );
      const excludedIds = new Set(
        groupToEdit.AlarmExclusions.map((ex) => ex.AlarmType.AlarmTypeId),
      );
      const included = alarmTypeOptions
        .filter((type) => !excludedIds.has(type.AlarmTypeId))
        .map((type) => type.AlarmTypeId);
      setAlarmTypes(included);
    } else {
      setGroupName('');
      setUsers([]);
      setAlarmTypes([]);
    }
  }, [groupToEdit, alarmTypeOptions, userOptions]);

  const handleClose = () => {
    setGroupToEdit(null);
    setGroupToDelete(null);
    setGroupName('');
    setUsers([]);
    setAlarmTypes([]);
    onClose();
  };

  const handleAddUser = () => {
    setUsers([...users, { user: '', feedSeverity: '', emailSeverity: '' }]);
  };

  const handleUserChange = (index, field, value) => {
    const updatedUsers = [...users];
    updatedUsers[index][field] = value;
    setUsers(updatedUsers);
  };

  const handleRemoveUser = (index) => {
    setUsers(users.filter((_, i) => i !== index));
  };

  const handleUpdate = () => {
    const AlarmSubscriptions = users
      .filter((u) => !!u.user)
      .map((u) => {
        const matchedUser = userOptions.find(
          (opt) => opt.User.Email === u.user,
        );
        return {
          User: { UserId: matchedUser?.User?.UserId ?? 0 },
          FeedSeverity: u.feedSeverity || 'Information',
          EmailSeverity: u.emailSeverity || 'Information',
        };
      });

    const selectedIds = new Set(alarmTypes);
    const AlarmExclusions = alarmTypeOptions
      .filter((type) => !selectedIds.has(type.AlarmTypeId))
      .map((type) => ({ AlarmType: { AlarmTypeId: type.AlarmTypeId } }));

    const payload = {
      AlarmGroupId: groupToEdit.AlarmGroupId,
      Name: groupName,
      AlarmSubscriptions,
      AlarmExclusions,
    };

    onUpdate(payload);
    handleClose();
  };

  const handleDelete = () => {
    if (!groupToDelete) return;
    onDelete(groupToDelete.AlarmGroupId);
    handleClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {groupToEdit && `Rediger Alarmgruppe: ${groupToEdit?.Name || ''}`}
          {groupToDelete && `Slet Alarmgruppe: ${groupToDelete?.Name || ''}`}
          {!groupToEdit && !groupToDelete && 'Alarmgrupper'}
          <IconButton
            onClick={handleClose}
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {!groupToEdit && !groupToDelete ? (
            <List>
              {alarmGroups?.map((group) => (
                <ListItem
                  key={group.AlarmGroupId}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    mb: 1,
                  }}
                  secondaryAction={
                    <>
                      <IconButton>
                        <Edit onClick={() => setGroupToEdit(group)} />
                      </IconButton>
                      <IconButton>
                        <DeleteRoundedIcon
                          onClick={() => setGroupToDelete(group)}
                        />
                      </IconButton>
                    </>
                  }
                >
                  <ListItemText
                    primary={group.Name}
                    secondary={
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {group.AlarmSubscriptions.map((sub) => {
                          const user = userOptions.find(
                            (u) => u.User.UserId === sub.User.UserId,
                          );
                          return (
                            <Chip
                              key={sub.User.UserId}
                              sx={{ borderRadius: 1 }}
                              label={user?.User?.Email || 'Ukendt bruger'}
                              size="small"
                            />
                          );
                        })}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : groupToEdit ? (
            <>
              <Box mb={3}>
                <TextField
                  fullWidth
                  label="Group Name"
                  size="small"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                />
              </Box>

              <Box mb={3}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="subtitle1">Users</Typography>
                  <Button
                    variant="text"
                    startIcon={<Add />}
                    onClick={handleAddUser}
                    sx={{ textTransform: 'none', borderRadius: 2 }}
                  >
                    Add User
                  </Button>
                </Box>

                {users.map((entry, index) => (
                  <Grid
                    container
                    spacing={2}
                    key={index}
                    alignItems="center"
                    sx={{ mb: 1, width: '100%' }}
                  >
                    <Grid item xs={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>User</InputLabel>
                        <Select
                          value={entry.user}
                          label="User"
                          onChange={(e) =>
                            handleUserChange(index, 'user', e.target.value)
                          }
                        >
                          {userOptions?.map((user) => (
                            <MenuItem
                              key={user.User.UserId}
                              value={user.User.Email}
                            >
                              {user.User.Email}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs>
                      <FormControl fullWidth size="small">
                        <InputLabel>Feed Threshold</InputLabel>
                        <Select
                          labelId={`feed-label-${index}`}
                          value={entry.feedSeverity}
                          label="Feed Threshold"
                          onChange={(e) =>
                            handleUserChange(
                              index,
                              'feedSeverity',
                              e.target.value,
                            )
                          }
                          renderValue={(selected) => (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                borderLeft: '5px solid',
                                borderColor:
                                  SEVERITY_COLORS[selected] || 'transparent',
                                pl: 1,
                              }}
                            >
                              <MonitorRoundedIcon fontSize="small" />
                              {selected}
                            </Box>
                          )}
                        >
                          {SEVERITY_LEVELS.map((level) => (
                            <MenuItem
                              key={level}
                              value={level}
                              sx={{
                                borderLeft: '5px solid',
                                borderColor: SEVERITY_COLORS[level],
                              }}
                            >
                              {level}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs>
                      <FormControl fullWidth size="small">
                        <InputLabel>Email Threshold</InputLabel>
                        <Select
                          labelId={`email-label-${index}`}
                          value={entry.emailSeverity}
                          label="Email Threshold"
                          onChange={(e) =>
                            handleUserChange(
                              index,
                              'emailSeverity',
                              e.target.value,
                            )
                          }
                          renderValue={(selected) => (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                borderLeft: '5px solid',
                                borderColor:
                                  SEVERITY_COLORS[selected] || 'transparent',
                                pl: 1,
                                height: '100%',
                              }}
                            >
                              <EmailRoundedIcon fontSize="small" />
                              {selected}
                            </Box>
                          )}
                        >
                          {SEVERITY_LEVELS.map((level) => (
                            <MenuItem
                              dense
                              key={level}
                              value={level}
                              sx={{
                                borderLeft: '5px solid',
                                borderColor: SEVERITY_COLORS[level],
                              }}
                            >
                              {level}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={0.5}>
                      <IconButton
                        onClick={() => handleRemoveUser(index)}
                        sx={{
                          p: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                        }}
                      >
                        <Close />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
              </Box>

              <Box>
                <Typography variant="subtitle1" mb={1}>
                  Alarm Types
                </Typography>
                <FormControl fullWidth size="small">
                  <InputLabel>Alarm Types</InputLabel>
                  <Select
                    multiple
                    value={alarmTypes}
                    onChange={(e) => setAlarmTypes(e.target.value)}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const matched = alarmTypeOptions.find(
                            (opt) => opt.AlarmTypeId === value,
                          );
                          return (
                            <Chip
                              key={value}
                              label={matched ? matched.Name : value}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {alarmTypeOptions?.map((type) => (
                      <MenuItem key={type.AlarmTypeId} value={type.AlarmTypeId}>
                        {type.Name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </>
          ) : groupToDelete ? (
            <Box>
              <Typography variant="body1">
                Er du sikker på, at du vil slette alarmgruppen{' '}
                <strong>{groupToDelete.Name}</strong>? Denne handling kan ikke
                fortrydes.
              </Typography>
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Brugere i gruppen:
                </Typography>
                <Stack spacing={0.5}>
                  {groupToDelete.AlarmSubscriptions.map((sub) => {
                    const user = userOptions.find(
                      (u) => u.User.UserId === sub.User.UserId,
                    );
                    return (
                      <Chip
                        key={sub.User.UserId}
                        label={user?.User?.Email || 'Ukendt bruger'}
                        size="small"
                      />
                    );
                  })}
                </Stack>
              </Box>
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Inkluderede alarmtyper:
                </Typography>
                <Stack spacing={0.5}>
                  {alarmTypeOptions
                    .filter(
                      (type) =>
                        !groupToDelete.AlarmExclusions.some(
                          (ex) => ex.AlarmType.AlarmTypeId === type.AlarmTypeId,
                        ),
                    )
                    .map((type) => (
                      <Chip
                        key={type.AlarmTypeId}
                        label={type.Name}
                        size="small"
                      />
                    ))}
                </Stack>
              </Box>
            </Box>
          ) : null}
        </DialogContent>

        {groupToEdit && (
          <DialogActions>
            <Button variant="text" onClick={() => setGroupToEdit(null)}>
              Afbryd
            </Button>
            <Button
              variant="contained"
              onClick={handleUpdate}
              disabled={!groupName || !users.length || !groupToEdit}
            >
              Opdater
            </Button>
          </DialogActions>
        )}

        {groupToDelete && (
          <DialogActions>
            <Button onClick={() => setGroupToDelete(null)} variant="text">
              Afbryd
            </Button>
            <Button variant="contained" onClick={handleDelete} color="error">
              Slet Alarmgruppe
            </Button>
          </DialogActions>
        )}

        {!groupToEdit && !groupToDelete && (
          <DialogActions>
            <Button onClick={handleClose} variant="text">
              Afbryd
            </Button>
            <Button variant="contained" onClick={openCreate}>
              Opret ny alarmgruppe
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};

export default EditAlarmGroupDialog;
